import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { LayoutNesting } from "#modules/layouts/infra/LayoutNesting.dto";
import { CanvasSection } from "#modules/layouts/view/CanvasSection";
import { useQuoteRequestLineContent } from "#modules/quoteRequestLines/domain/useQuoteRequestLineContent";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PartHeader } from "#modules/quoteRequestLines/views/QuoteRequestLine/PartHeader";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { formatMaterialDimensions, PartTypeSchema } from "shared";

import { LayoutDto } from "../infra/fetchLayout.api";

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  onSave: () => void;
  hasChildren: boolean;
  isChild: boolean;
  ConfirmComponent?: FunctionComponent;
  layout: LayoutDto;
  handleSetCurrentLayoutNesting?: (layoutNesting: LayoutNesting) => void;
  currentLayoutNesting?: LayoutNesting | null;
};

export const LayoutComputingForm = ({
  quoteRequestLine,
  hasChildren,
  isChild,
  ConfirmComponent = () => <></>,
  onSave,
  layout,
  currentLayoutNesting,
  handleSetCurrentLayoutNesting,
}: Props) => {
  const { t } = useTranslation();
  const [isLayoutComputed, setIsLayoutComputed] = useState(false);
  const { isConsumable } = useQuoteRequestLineContent({
    quoteRequestLine,
    part: quoteRequestLine.part,
    hasChildren,
    isChild,
  });

  const materialLayout = layout.materialLayout;
  const materialGrade = quoteRequestLine.materialGrade;
  const materialName =
    quoteRequestLine.materialName ||
    (materialGrade ? t(`qrl.materialGrades.${materialGrade}`) : "-");

  return (
    <>
      <PartHeader
        hasChildren={hasChildren}
        quoteRequestLine={quoteRequestLine}
        isConsumable={isConsumable}
        fontSize={16}
        thumbnailSize={{ width: 55, height: 55 }}
        part={quoteRequestLine.part}
        showMaterial={false}
      />

      <Space h="sm" />

      {materialLayout && (
        <Stack gap={0}>
          <Text>
            {t("qrl.materialSpecification")} : <b>{materialName}</b>
          </Text>
          <Text>
            {t("qrl.dimensions.materialDimensions")} :{" "}
            <b>
              {formatMaterialDimensions(
                materialLayout.type || quoteRequestLine.part.type || PartTypeSchema.enum.OTHER,
                materialLayout,
              )}
            </b>
          </Text>
        </Stack>
      )}

      <Space h="sm" />

      <Stack gap="xxxs">
        <CanvasSection
          isLayoutComputed={isLayoutComputed}
          setIsLayoutComputed={setIsLayoutComputed}
          handleSetCurrentLayoutNesting={handleSetCurrentLayoutNesting}
          currentLayoutNesting={currentLayoutNesting}
          layout={layout}
          quoteRequestLineId={quoteRequestLine.id}
        />
      </Stack>

      <Space h="sm" />
      <Group justify="right">
        <Button onClick={onSave}>{t("common.validateNesting")}</Button>
      </Group>
      <ConfirmComponent />
    </>
  );
};
